import { z } from 'zod';
import { isAfter } from 'date-fns';

import {
  convertSendAtToDBValue,
  convertSendAtToFormValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import { parseUTCDate } from 'utils/dateTimeUtils';

/**
 * Registration Cutoff Modal Schema
 *
 * Form Format (browser timezone):
 * {
 *   hasRegistrationCutoffTime: boolean;
 *   registrationCutoffAt: Date | null;  // Date object in browser's timezone
 * }
 *
 * DB Format (UTC):
 * {
 *   hasRegistrationCutoffTime: boolean;
 *   registrationCutoffAt: string | null;  // UTC string (e.g., '2024-01-15 10:30:00')
 * }
 *
 * The schema transforms Date → UTC string, then validates in UTC for simplicity.
 */

export const createRegistrationCutoffModalSchema = ({
  eventTimezone,
  datetimeEnd
}: {
  eventTimezone: string;
  datetimeEnd?: string | null;
}) =>
  z
    .object({
      hasRegistrationCutoffTime: z.boolean(),
      registrationCutoffAt: z
        .date()
        .nullable()
        // Transform: Convert Date (browser timezone) → UTC string for DB
        .transform((date: Date | null): string | null =>
          date ? convertSendAtToDBValue(date, eventTimezone) : null
        )
        // Validation: Must be before event end
        .refine(
          (utcString) => {
            if (!utcString || !datetimeEnd) {
              return true;
            }

            const registrationCutoffAtUTC = parseUTCDate(utcString);
            const eventEndUTC = parseUTCDate(datetimeEnd);

            return !isAfter(registrationCutoffAtUTC, eventEndUTC);
          },
          {
            message: 'Registration cutoff must be before the event ends'
          }
        )
    })
    // Object-level validation: Ensure date is provided when toggle is enabled
    .refine(
      (data) => {
        if (data.hasRegistrationCutoffTime && !data.registrationCutoffAt) {
          return false;
        }
        return true;
      },
      {
        message: 'Date and time are required',
        path: ['registrationCutoffAt']
      }
    );

// Form input type: What the form receives (Date objects in browser timezone)
export type RegistrationCutoffModalFormInput = z.input<
  ReturnType<typeof createRegistrationCutoffModalSchema>
>;

// Form output type: What gets sent to the backend (UTC strings)
export type RegistrationCutoffModalFormValues = z.infer<
  ReturnType<typeof createRegistrationCutoffModalSchema>
>;

/**
 * Convert database values to form input values
 * Transforms UTC strings from DB → Date objects in browser timezone for the form
 *
 * @param dbData - Data from database with UTC strings
 * @param eventTimezone - Event timezone for proper conversion
 * @returns Form input values with Date objects
 */
export const convertRegistrationCutoffDBToFormData = (
  dbData: { hasRegistrationCutoffTime: boolean; registrationCutoffAt?: string | null },
  eventTimezone: string
): RegistrationCutoffModalFormInput => {
  return {
    hasRegistrationCutoffTime: dbData.hasRegistrationCutoffTime,
    registrationCutoffAt: dbData.registrationCutoffAt
      ? convertSendAtToFormValue(dbData.registrationCutoffAt, eventTimezone)
      : null
  };
};
